// src/modulos/Parametros/Tipos/TypePaymentDocPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useSubCollection from '../../../hooks/useSubCollection';
import usePagePermissions from '../../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../../components/common';

// Import utilities
import { getTimestamps } from '../../../utils/timestamps';

// Page-specific styles
import styles from '../Parameters.module.css';

// CONSTANTES
const PAYMENT_DOC_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'documento-pagamento'
};
const ITEMS_PER_PAGE = 10;

const CARD_FLAGS = ['Visa', 'Mastercard', 'American Express', 'Sorocred', 'Outros'];

const REMITTANCE_TYPES = [
    'TED', 'DOC', 'Transferência CC', 'Transferência PP', 'Boleto',
    'Imposto CB', 'Imposto sem CB (GPS)', 'Imposto sem CB (DARF SIMPLES)',
    'Imposto sem CB (DAF)', 'Concessionárias'
];

const initialFormData = {
    nome: '',
    floatBancario: '',
    consideraCredito: true,
    isCardType: false,
    credenciadoraCnpj: '',
    bandeiraCartao: CARD_FLAGS[0],
    tipoRemessaPagamento: REMITTANCE_TYPES[0]
};

const TypePaymentDocPage = () => {
    const can = usePagePermissions('tipos_de_doc_de_pagamento');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: paymentDocs,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useSubCollection(
        PAYMENT_DOC_COLLECTION_PATH.parentCollection,
        PAYMENT_DOC_COLLECTION_PATH.parentId,
        PAYMENT_DOC_COLLECTION_PATH.subCollectionName,
        'nome'
    );

    // ============================================================
    // 1. DEFINIR filteredResults ANTES dos callbacks
    // ============================================================
    const filteredResults = useMemo(() => {
        if (!paymentDocs) return [];
        if (!searchTerm) return paymentDocs;

        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return paymentDocs.filter(item =>
            item.nome.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [paymentDocs, searchTerm]);

    const totalFilteredItems = filteredResults.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const paginatedResults = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredResults.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredResults, currentPage]);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setFormData(initialFormData);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setFormData({
            nome: item.nome || '',
            floatBancario: item.floatBancario || '',
            consideraCredito: item.consideraCredito !== undefined ? item.consideraCredito : true,
            isCardType: item.isCardType !== undefined ? item.isCardType : false,
            credenciadoraCnpj: item.credenciadoraCnpj || '',
            bandeiraCartao: item.bandeiraCartao || CARD_FLAGS[0],
            tipoRemessaPagamento: item.tipoRemessaPagamento || REMITTANCE_TYPES[0]
        });
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Documento "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar documento. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, totalFilteredItems, currentPage]);

    const handleChange = useCallback((e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value
        }));
    }, []);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!formData.nome.trim()) {
            setFormError("O nome do Tipo de Documento é obrigatório.");
            return;
        }

        if (!formData.floatBancario || isNaN(parseInt(formData.floatBancario))) {
            setFormError("O Float Bancário deve ser um número válido (em dias).");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);

        let dataToSave = {
            ...formData,
            nome: formData.nome.trim(),
            floatBancario: parseInt(formData.floatBancario),
            ...timestamps,
        };

        if (!formData.isCardType) {
            delete dataToSave.credenciadoraCnpj;
            delete dataToSave.bandeiraCartao;
        }

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, dataToSave);
            } else {
                result = await addDocument(dataToSave);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Documento editado com sucesso!' : 'Documento adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [formData, editingItem, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.nome}` : 'Adicionar Novo Documento de Pagamento'}
                subtitle={editingItem ? `Editando: ${editingItem.nome}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Documento'}
            >
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="nome">
                            Nome do Documento <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="nome"
                            type="text"
                            value={formData.nome}
                            onChange={handleChange}
                            className={styles.input}
                            placeholder="Ex: Cartão de Crédito, Boleto, Cheque"
                            required
                            disabled={formLoading}
                            autoFocus
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="floatBancario">
                            Float Bancário (dias) <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="floatBancario"
                            type="number"
                            min="0"
                            value={formData.floatBancario}
                            onChange={handleChange}
                            className={styles.input}
                            placeholder="Ex: 30"
                            required
                            disabled={formLoading}
                        />
                        <small className={styles.fieldHint}>Número de dias para compensação</small>
                    </div>
                </div>

                <div className={styles.inputGroup}>
                    <label className={styles.checkboxLabel}>
                        <input
                            id="consideraCredito"
                            type="checkbox"
                            checked={formData.consideraCredito}
                            onChange={handleChange}
                            disabled={formLoading}
                        />
                        <span>Considera na Avaliação de Crédito?</span>
                    </label>
                </div>

                <div className={styles.formSection}>
                    <h3>Remessa Bancária</h3>
                    <div className={styles.inputGroup}>
                        <label htmlFor="tipoRemessaPagamento">Tipo de Remessa</label>
                        <select
                            id="tipoRemessaPagamento"
                            value={formData.tipoRemessaPagamento}
                            onChange={handleChange}
                            className={styles.select}
                            disabled={formLoading}
                        >
                            {REMITTANCE_TYPES.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.formSection}>
                    <h3>Configuração Cartão</h3>

                    <div className={styles.inputGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                id="isCardType"
                                type="checkbox"
                                checked={formData.isCardType}
                                onChange={handleChange}
                                disabled={formLoading}
                            />
                            <span>É um Tipo de Cartão?</span>
                        </label>
                    </div>

                    {formData.isCardType && (
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label htmlFor="bandeiraCartao">Bandeira do Cartão</label>
                                <select
                                    id="bandeiraCartao"
                                    value={formData.bandeiraCartao}
                                    onChange={handleChange}
                                    className={styles.select}
                                    disabled={formLoading}
                                >
                                    {CARD_FLAGS.map(flag => (
                                        <option key={flag} value={flag}>{flag}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.inputGroup}>
                                <label htmlFor="credenciadoraCnpj">CNPJ Credenciadora</label>
                                <input
                                    id="credenciadoraCnpj"
                                    type="text"
                                    value={formData.credenciadoraCnpj}
                                    onChange={handleChange}
                                    className={styles.input}
                                    placeholder="Ex: 00.000.000/0001-00"
                                    disabled={formLoading}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o documento "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Tipos de Documento de Pagamento"
                actionText="Adicionar Documento"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar tipo de documento..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={5} rows={ITEMS_PER_PAGE} />
                    ) : paginatedResults.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '💳'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum documento cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}"`
                                    : "Clique em '+ Adicionar Documento' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome</th>
                                            <th>Float (dias)</th>
                                            <th>Considera Crédito</th>
                                            <th>Tipo de Remessa</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedResults.map((doc) => (
                                            <tr key={doc.id} className={styles.tableRow}>
                                                <td><strong>{doc.nome}</strong></td>
                                                <td>{doc.floatBancario}</td>
                                                <td>{doc.consideraCredito ? '✅ Sim' : '❌ Não'}</td>
                                                <td>{doc.tipoRemessaPagamento}</td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(doc)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(doc.id, doc.nome)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="documentos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default TypePaymentDocPage;